import { Alert, AlertDescription, AlertIcon } from "@chakra-ui/alert";
import { WarningTwoIcon } from "@chakra-ui/icons";
import {
  Card,
  CardBody,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  getCapitalCallFeeBasisNotificationTableDataRetrieveQueryKey,
  useCapitalCallCreditFacilityReallocationCreate,
  useCapitalCallFeeBasisNotificationTableDataRetrieveSuspense,
} from "gen/endpoints/capital-call/capital-call";
import { useFundFamilyEventRetrieveSuspense } from "gen/endpoints/fund-family-event/fund-family-event";
import { useInvestmentListSuspense } from "gen/endpoints/investment/investment";
import { CapitalCallCFReallocationCreateDataRequestInvestmentIdToLoanIdToCfCodeToAmount } from "gen/model/capitalCallCFReallocationCreateDataRequestInvestmentIdToLoanIdToCfCodeToAmount";
import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Form from "shared/components/form/Form";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import {
  PageWizardFormFooter,
  usePageWizard,
} from "shared/components/layout/PageWizard";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { formatIsoDateToDisplay } from "shared/utils/DateUtils";
import { formatMoney } from "shared/utils/MoneyUtils";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";

export const FeeBasisNotification: React.FC = () => {
  const { t } = useTranslation(["common", "capitalCall", "allocation"]);
  const queryClient = useQueryClient();

  const { fundFamilyId, fundFamilyEventId } = useRequiredStringParams();

  const { data: fundFamilyEventData } = useFundFamilyEventRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
  );

  const { mutateAsync: createCapitalCallCreditFacilityReallocation } =
    useCapitalCallCreditFacilityReallocationCreate();

  const { data: feeBasisData } =
    useCapitalCallFeeBasisNotificationTableDataRetrieveSuspense(
      fundFamilyId,
      fundFamilyEventId,
    );

  const { data: investmentData } = useInvestmentListSuspense(fundFamilyId);

  const investmentIdToName: Record<string, string> = Object.fromEntries(
    investmentData.map((investment) => [investment.id, investment.legal_name]),
  );

  const onSubmit = async (
    data: CapitalCallCFReallocationCreateDataRequestInvestmentIdToLoanIdToCfCodeToAmount,
  ) => {
    await createCapitalCallCreditFacilityReallocation({
      fundFamilyId,
      fundFamilyEventId,
      data: {
        investment_id_to_loan_id_to_cf_code_to_amount: data,
      },
    });
    await invalidateQueryKeys(
      queryClient,
      getCapitalCallFeeBasisNotificationTableDataRetrieveQueryKey(
        fundFamilyId,
        fundFamilyEventId,
      ),
    );
  };

  const form =
    useForm<CapitalCallCFReallocationCreateDataRequestInvestmentIdToLoanIdToCfCodeToAmount>();
  const { navigateNext } = usePageWizard();

  return (
    <PageContent>
      <PageHeader title={t("capitalCall:feeBasisNotification.handle")} />
      <PageBody>
        <Alert
          status="warning"
          mb="24px"
          data-ls-enabled={true}
          data-public={true}
        >
          <AlertIcon>
            <WarningTwoIcon />
          </AlertIcon>
          <AlertDescription>
            {t("capitalCall:feeBasisNotification.alert")}
          </AlertDescription>
        </Alert>
        <Card mb="16px">
          <CardBody>
            <Text mb="16px">
              {t("capitalCall:feeBasisNotification.description", {
                effective_date: formatIsoDateToDisplay(
                  fundFamilyEventData.effective_date,
                ),
              })}
            </Text>
            <Text mb="8px" textStyle="bodyBold">
              {t("capitalCall:feeBasisNotification.overlapping_use_of_funds")}
            </Text>
            <TableContainer>
              <Table variant="lightweight" size="lightweight">
                <Thead>
                  <Tr>
                    <Th>
                      {t("capitalCall:feeBasisNotification.use_of_funds")}
                    </Th>
                    <Th textAlign="end">
                      {t(
                        "capitalCall:feeBasisNotification.current_capital_call",
                      )}
                    </Th>
                    <Th textAlign="end">
                      {t(
                        "capitalCall:feeBasisNotification.outstanding_credit_facility",
                      )}
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {Object.entries(
                    feeBasisData.capital_call_credit_facility_overlap_data
                      .investment_id_to_call_amount,
                  ).map(([investmentId, callAmount]) => (
                    <Tr key={investmentId}>
                      <Th>{investmentIdToName[investmentId]}</Th>
                      <Td textAlign="end">{formatMoney(callAmount)}</Td>
                      <Td textAlign="end">
                        {formatMoney(
                          feeBasisData.capital_call_credit_facility_overlap_data
                            .investment_id_to_cf_amount[investmentId],
                        )}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>
        <Form form={form} onSubmit={onSubmit} onSuccess={navigateNext}>
          <TableBox isFullWidth={true}>
            <TableBoxHeader>
              <TableBoxTitle>
                {t("capitalCall:feeBasisNotification.title")}
              </TableBoxTitle>
            </TableBoxHeader>
            <TableBoxBody>
              <DataTable data={feeBasisData.fee_basis_notification_table} />
            </TableBoxBody>
          </TableBox>
        </Form>
      </PageBody>
      <PageWizardFormFooter />
    </PageContent>
  );
};
